import { useMemo } from 'react'
import { LocationTypesEnum } from '../../../pages/adgroup/settings/geoTargeting/GeoTargetingFields'
import {
  BoundaryFeature,
  BoundaryKind,
  findFeatureNearPoint,
  findZoneFeature,
  hasBoundaries,
  useBoundaries,
} from '../../../utils/geo/boundaries'
import { GeoLocation, LocationArea } from './types'

/** Translation key (under adgroup.geoTargeting.panel) of the area column's header */
export type AreaColumn = 'columnDepartement' | 'columnRegion' | 'columnCodeRegion'

export interface LocationAreas {
  /** Undefined when the market has no borders: the column is hidden */
  column?: AreaColumn
  /** Aligned with the Formik array; undefined while the borders load */
  areas?: Array<LocationArea | undefined>
}

const pointCache = new WeakMap<BoundaryFeature[], Map<string, BoundaryFeature | null>>()

// Cached by coordinates, so editing a radius, filtering or paging never runs the point-in-polygon test again
const featureAt = (latitude: number, longitude: number, features: BoundaryFeature[]): BoundaryFeature | undefined => {
  let cache = pointCache.get(features)
  if (!cache) {
    cache = new Map()
    pointCache.set(features, cache)
  }
  const key = `${latitude},${longitude}`
  if (!cache.has(key)) cache.set(key, findFeatureNearPoint(latitude, longitude, features) ?? null)
  return cache.get(key) ?? undefined
}

/** INSEE code of the région of each metropolitan département: fixed since the 2016 reform, so a lookup beats geometry */
const REGION_OF_DEPARTEMENT: Record<string, string> = Object.fromEntries(
  Object.entries({
    '11': '75 77 78 91 92 93 94 95',
    '24': '18 28 36 37 41 45',
    '27': '21 25 39 58 70 71 89 90',
    '28': '14 27 50 61 76',
    '32': '02 59 60 62 80',
    '44': '08 10 51 52 54 55 57 67 68 88',
    '52': '44 49 53 72 85',
    '53': '22 29 35 56',
    '75': '16 17 19 23 24 33 40 47 64 79 86 87',
    '76': '09 11 12 30 31 32 34 46 48 65 66 81 82',
    '84': '01 03 07 15 26 38 42 43 63 69 73 74',
    '93': '04 05 06 13 83 84',
    '94': '2A 2B',
  }).flatMap(([region, departements]) => departements.split(' ').map((departement) => [departement, region]))
)

const hasPoint = (
  location: GeoLocation
): location is GeoLocation & { location: { latitude: number; longitude: number } } =>
  typeof location.location?.latitude === 'number' && typeof location.location?.longitude === 'number'

const getCityKind = (countryCode?: string): BoundaryKind =>
  countryCode?.toLowerCase() === 'fr' ? 'departements' : 'regions'

/**
 * The area of each location, from the market's border files (loaded once, lazily):
 * - cities: their département on the French market, their region on the other European markets;
 * - départements: their code and the région they belong to (French market);
 * - regions and countries: none.
 */
export function useLocationAreas(
  locations: GeoLocation[],
  locationType: LocationTypesEnum,
  countryCode?: string
): LocationAreas {
  const isCities = locationType === LocationTypesEnum.Cities
  const isDepartements = locationType === LocationTypesEnum.Departements && countryCode?.toLowerCase() === 'fr'

  const primaryKind: BoundaryKind | undefined = isCities
    ? getCityKind(countryCode)
    : isDepartements
      ? 'departements'
      : undefined
  const primaryEnabled = Boolean(primaryKind) && hasBoundaries(primaryKind as BoundaryKind, countryCode)
  const primary = useBoundaries(primaryKind, countryCode, primaryEnabled)
  const regions = useBoundaries('regions', 'fr', isDepartements)

  return useMemo(() => {
    if (!primaryEnabled) return {}

    if (isCities) {
      const isFrance = primaryKind === 'departements'
      const column: AreaColumn = isFrance ? 'columnDepartement' : 'columnRegion'
      if (!primary) return { column }
      return {
        column,
        areas: locations.map((location) => {
          if (!hasPoint(location)) return undefined
          const feature = featureAt(location.location.latitude, location.location.longitude, primary)
          if (!feature) return undefined
          const { code, nom } = feature.properties
          return { code, label: isFrance ? `${code} · ${nom}` : nom }
        }),
      }
    }

    // Départements: the code, and its région. The centre of a coastal département's border can be at sea (13), so no geometry here
    if (!primary || !regions) return { column: 'columnCodeRegion' }
    return {
      column: 'columnCodeRegion',
      areas: locations.map((zone) => {
        const feature = findZoneFeature(zone, primary)
        if (!feature) return undefined
        const { code } = feature.properties
        const region = regions.find((candidate) => candidate.properties.code === REGION_OF_DEPARTEMENT[code])
        return { code, label: region ? `${code} · ${region.properties.nom}` : code, group: region?.properties.nom }
      }),
    }
  }, [locations, isCities, primaryEnabled, primaryKind, primary, regions])
}
